import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, CreditCard, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { PrepChecklist } from "@/components/PrepChecklist";
import { MemberSavings } from "@/components/booking/MemberSavings";

function PaymentLine({ b, token }) {
  if (b.payment_status === "paid") return <span className="dg-chip text-emerald-300">Paid ${Number(b.deposit_usd).toFixed(2)}</span>;
  if (b.payment === "gift") return <span className="dg-chip">Covered by gift code</span>;
  if (b.payment === "membership") return <span className="dg-chip text-[#FFD600]">Included in your membership</span>;
  return (
    <div className="text-slate-300">
      Nothing to pay today — after your session David emails you a secure card link for <strong className="text-white">${Number(b.amount_due || b.deposit_usd).toFixed(2)}</strong>.{" "}
      <Link to={`/pay/${token}`} className="dg-link inline-flex items-center gap-1" data-testid="pay-ahead-link"><CreditCard className="w-4 h-4" /> Prefer to pay ahead?</Link>
    </div>
  );
}

export default function BookingConfirmed() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/bookings/public/${token}`).then((r) => setB(r.data)).catch(() => setError("We couldn't find that booking."));
  }, [token]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="booking-notfound">{error} <Link to="/book" className="dg-link">Book a session</Link></div>;
  if (!b) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading your booking…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="booking-confirmed">
      <div className="dg-card rounded-2xl p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
        <div className="dg-eyebrow mt-4">{b.status === "cancelled" ? "Cancelled" : "Confirmed"}</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold font-display">{b.status === "cancelled" ? "This session was cancelled." : "You're all set!"}</h1>
        <p className="text-slate-300 mt-3"><strong className="text-white">{b.service_name}</strong><br />{b.when}</p>
        <p className="text-slate-400 mt-2 text-sm">{b.meeting_method} · Booking <span className="font-mono text-cyan-300">{b.booking_id}</span></p>
        <div className="mt-5 text-sm" data-testid="payment-status"><PaymentLine b={b} token={token} /></div>
        <MemberSavings tierName={b.member_tier_name} saved={b.member_saved_usd} savedMonth={b.member_saved_month_usd} />
        {b.bonus_minutes > 0 && <div className="mt-3 rounded-xl border border-[#FFD600]/40 bg-[#FFD600]/10 px-4 py-3 text-sm text-[#FFD600] font-semibold" data-testid="booking-bonus-minutes">+{b.bonus_minutes} bonus minutes on this session — your {b.bonus_reason || "birthday"} gift from David.</div>}
        <p className="text-xs text-slate-500 mt-6">A confirmation email with these details is in your inbox.</p>
        <Link to={`/book/manage/${token}`} className="dg-btn-ghost rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 mt-4" data-testid="manage-booking-link">
          <CalendarClock className="w-4 h-4" /> Reschedule or cancel
        </Link>
      </div>
      <div className="mt-8"><PrepChecklist /></div>
    </div>
  );
}
